// Client CRUD schemas — plan §4.3 + §11.2
import { z } from "zod";

// §4.3.3 sektör + büyüklük — free text (admin seçim listesi M8 Ayarlar'da editable).
// M2-S2: primary_contact_* wizard step 2 için eklendi (nullable, opsiyonel).
export const ClientSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sector: z.string().nullable(),
  country: z.string().nullable(),
  size: z.string().nullable(),
  primaryContactName: z.string().nullable(),
  primaryContactEmail: z.string().nullable(),
  primaryContactPhone: z.string().nullable(),
  createdById: z.string().uuid(),
  createdByName: z.string(),
  deletedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  projectCount: z.number().int(),
  // M17 T5 (0058) — mali yıl başlangıç ayı; 1 = takvim yılı. KPI değeri varken değiştirilemez (409).
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  /** Yalnız GET/PATCH /clients/:id — kartın kilidi için (müşterinin kpi_values satır sayısı). */
  kpiValueCount: z.number().int().optional(),
});
export type Client = z.infer<typeof ClientSchema>;

// Paginated list response — §4.3.1 filtre + sayfalama.
// `clients` field'ı geri uyumluluk için tutuluyor; yeni kullanımda `items` tercih edin.
export const ClientsListResponseSchema = z.object({
  clients: z.array(ClientSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type ClientsListResponse = z.infer<typeof ClientsListResponseSchema>;

// Query params — §4.3.1 filtreler
export const ClientsListQuerySchema = z.object({
  search: z.string().optional(),
  sector: z.string().optional(),
  size: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  // Tavan 200 → 500: toplu rapor aktarımı ekranı mükerrer müşteri kaydını önlemek
  // için TÜM kayıtları tek sayfada görmek zorunda (satır içi "+ Yeni müşteri"
  // benzerlik uyarısı yüklenmiş listeye bakar). Varsayılan 25 aynı kaldı.
  limit: z.coerce.number().int().min(1).max(500).default(25),
  includeDeleted: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
});
export type ClientsListQuery = z.infer<typeof ClientsListQuerySchema>;

// GET /clients/facets — liste filtreleri için distinct sektör/boyut (silinmemiş kayıtlar).
// ClientsList facet'leri daha önce yalnız görünen 25 satırdan türetiliyordu; bu uç
// tüm kayıtları kapsar (ClientPicker işi, 2026-10-07).
export const ClientFacetsResponseSchema = z.object({
  sectors: z.array(z.string()),
  sizes: z.array(z.string()),
});
export type ClientFacetsResponse = z.infer<typeof ClientFacetsResponseSchema>;

export const CreateClientRequestSchema = z.object({
  name: z.string().min(2, "Şirket adı en az 2 karakter"),
  sector: z.string().optional(),
  country: z.string().optional(),
  size: z.string().optional(),
  primaryContactName: z.string().optional(),
  primaryContactEmail: z
    .string()
    .email("Geçerli bir email girin")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  primaryContactPhone: z.string().optional(),
});
export type CreateClientRequest = z.infer<typeof CreateClientRequestSchema>;

// TODO-EVIDENCE-03 — müşteri bazlı kanıt politikası (clients.evidence_policy jsonb).
// NULL kayıt / eksik ya da null alan = sistem geneli ayar (evidence.retention_days /
// evidence.import_retention_days / evidence.default_confidentiality). 0 gün = temizlik kapalı.
export const ClientEvidencePolicySchema = z.object({
  retentionDays: z.number().int().min(0).max(36500).nullable().optional(),
  importRetentionDays: z.number().int().min(0).max(36500).nullable().optional(),
  defaultConfidentiality: z.enum(["internal", "client", "public"]).nullable().optional(),
});
export type ClientEvidencePolicy = z.infer<typeof ClientEvidencePolicySchema>;

/** GET/PUT /clients/:id/evidence-policy — `policy` null = override yok. */
export const ClientEvidencePolicyResponseSchema = z.object({
  clientId: z.string().uuid(),
  policy: ClientEvidencePolicySchema.nullable(),
  /** Sistem geneli değerler — kartta "sistem: 180 gün" ipucu için. */
  system: z.object({
    retentionDays: z.number().int(),
    importRetentionDays: z.number().int(),
    defaultConfidentiality: z.enum(["internal", "client", "public"]),
  }),
});
export type ClientEvidencePolicyResponse = z.infer<typeof ClientEvidencePolicyResponseSchema>;

export const UpdateClientRequestSchema = CreateClientRequestSchema.partial()
  .extend({ fiscalYearStartMonth: z.number().int().min(1).max(12).optional() })
  .refine((v) => Object.keys(v).length > 0, { message: "En az bir alan güncellenmeli" });
export type UpdateClientRequest = z.infer<typeof UpdateClientRequestSchema>;
